import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useRHFNestedFormContext } from '@openx/components-core/lib/Form/context/RHFNestedFormContext.tsx';
import { InfoTile } from '@openx/components-core/lib/InfoTile/InfoTile.tsx';
import { ACL_TYPE } from '@openx/types/aclType.ts';

import { dealFormField, type RboConfig, rboAlertKind } from '@openx/types/deal.ts';

import { PackageFields, type PackageFormState } from '@openx/types/package.ts';
import { TargetingFields } from '@openx/types/targeting/targeting.ts';
import type { RequiredTargetingFormType } from '@openx/types/targeting/targetingPresets.ts';
import { cleanUpNulls } from '@openx/utils/lib/cleanUpNulls.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { isInventoryFormatIncompatibleWithRbo } from '../../../../../utils/isTargetingRboCompatible.ts';
import { useRboDisplayedAlert } from '../RboDisplayedAlertContext.tsx';

type DisableRboAlertProps = {
  targetingParams?: RequiredTargetingFormType | null;
};

export const DisableRboAlert = ({ targetingParams }: DisableRboAlertProps) => {
  const { t } = useTranslation();
  const { useIsAllowed } = usePermissionContext();
  const { parentForm, isNested } = useRHFNestedFormContext();

  const { isAllowed } = useIsAllowed();

  const observedTargeting = useWatch<PackageFormState, typeof PackageFields.TARGETING>({
    name: PackageFields.TARGETING,
  });

  const resultsEnabled = useMemo(() => isAllowed(isAllowedByCompiledAcl(ACL_TYPE.RESULTS_ENABLED)), [isAllowed]);
  const rboDisplayed = useRboDisplayedAlert();

  if (!isNested || !resultsEnabled || rboDisplayed?.displayedKind === rboAlertKind.GROUP_VALIDATION) {
    return null;
  }

  const rboConfig: RboConfig = parentForm?.getValues(dealFormField.RBO_CONFIG) ?? null;

  const inventoryFormat = observedTargeting?.[TargetingFields.INVENTORY_FORMAT] ?? targetingParams ?? null;

  const hasRboValues = !!cleanUpNulls(rboConfig);

  if (
    !hasRboValues ||
    !isInventoryFormatIncompatibleWithRbo(inventoryFormat, {
      missingInventoryFormatIncompatible: true,
      missingAdPlacementIncompatible: false,
    })
  ) {
    return null;
  }

  return (
    <InfoTile
      data-test="disable-rbo-alert"
      severity="error"
      icon={<AutoAwesomeOutlinedIcon />}
      item={{
        title: t('Engagement Optimization'),
        body: (
          <Trans
            t={t}
            i18nKey="Engagement optimization is only applicable for <b>Banner</b> or <b>Video</b> Inventory Targeting, and will be disabled above upon saving deal."
            components={{ b: <strong /> }}
          >
            Engagement optimization is only applicable for <strong>Banner</strong> or <strong>Video</strong> Inventory
            Targeting, and will be disabled above upon saving deal.
          </Trans>
        ),
      }}
    />
  );
};
